import {
    decodeJwtWeb,
    IResponse,
    RegExpCheckEmail,
    SESSION_USER_CLAIMS,
    spLogTags,
} from '@eventr/common'
import {
    AccessToken,
    AppleProviderResponse,
    SocialLogin,
} from '../../plugins/socialLogin/SocialLoginPlugin'

import { Capacitor } from '@capacitor/core'
import { useToken } from '@eventr/common-storybook'
import ApiClient from '@eventr/common/lib/httpRequests/httpRequest'
import qs from 'qs'
import queryString from 'query-string'
import { v1 as uuidv1 } from 'uuid'
import { StateCreator } from 'zustand'
import { EventrGatewayAPI } from '../../data/gateway.api'
import { comboInterceptorResponse } from '../../data/interseptors/comboInterResp'
import { logInterReq } from '../../data/interseptors/logInterReq'
import { logInterReqError } from '../../data/interseptors/logInterReqError'
import { logInterResponseError } from '../../data/interseptors/logInterResponseError'
import i18n from '../../i18n/i18next'
import {
    entraLogin,
    EntraProviderResponse,
} from '../../plugins/socialLogin/browserOAuth'
import { Paths } from '../../routes'
import { BoundStore, bs, useBoundStore } from '../../state/bound.store'
import { defaultAddSnack } from '../../state/overlayState/overlayContext'
import { loadingSignal } from '../../state/overlayState/overlaySignals'
import { userPrefLoginMethod } from '../../state/userPrefsState/userPrefsSignels'
import { bytesToBase64, getRandomString } from '../../utils'
import { useRegStore } from '../authentication/registrationProvider'
import { logEvent } from '../firebase/firebase'
import { useGuestStore } from '../guest/guest.store'
import { oidcConfig } from './config'
import { OAUTH_ENDPOINTS } from './endpoints'
import {
    ICodeExchangeBody,
    IEmailSignInBody,
    ITokenResponse,
} from './interfaces'
interface ICredentials {
    email: string
    password?: string
    loading: boolean
    errors: Record<string, string>
}

interface OauthState {
    IDtoken: string
    refreshToken: string
    expiration: number
    credentials: ICredentials
    loginCompleted: boolean
}

interface GoogleLoginResponseOnline {
    accessToken: AccessToken | null
    idToken: string | null
    profile: {
        email: string | null
        familyName: string | null
        givenName: string | null
        id: string | null
        name: string | null
        imageUrl: string | null
    }
    responseType: 'online'
}
interface OauthActions {
    refreshTokens: () => Promise<IResponse<ITokenResponse>>

    googleOneTapSignIn: () => Promise<{
        googleResult: GoogleLoginResponseOnline
        resp: IResponse<ITokenResponse>
    }>
    appleOneTapSignIn: () => Promise<{
        appleResult: AppleProviderResponse
        resp: IResponse<ITokenResponse>
    }>
    entraOneTapSignIn: () => Promise<{
        entraResult: EntraProviderResponse
        resp: IResponse<ITokenResponse>
    }>
    signInWithPassword: (
        email: string,
        password: string,
        redirectPath?: string | null
    ) => Promise<IResponse<ITokenResponse>>
    emailSignIn: (email: string) => Promise<IResponse<null>>
    signInWithProvider: (provider: string) => void
    setTokens: (tokens: any) => void
    signInRedirect: () => void
    signIn: () => void
    exchangeCode: (code: string) => Promise<IResponse<ITokenResponse>>
}

const getSocialConfig = () => {
    const google = {
        webClientId:
            '974218315221-uj2ae1qq95larem28jf1v1d4jto0s6mn.apps.googleusercontent.com',
        iOSClientId:
            '974218315221-l5dpb7hadqbbdu1cq7qfgsenuhj2fs1p.apps.googleusercontent.com',
        mode: 'online',
        // iOSServerClientId
    }
    if (Capacitor.getPlatform() === 'ios')
        return {
            google: google,
            // apple needs config only for android
            apple: {
                clientId: 'com.softpauer.eventr.signin',
            },
            entra: {
                clientId: 'bad81977-84cc-4d19-9492-3828d507b848',
                mode: 'online',
            },
        }
    if (Capacitor.getPlatform() === 'android')
        return {
            google: google,
        }
    return {
        google: google,
        apple: {
            clientId: 'com.softpauer.eventr.signin',
        },
        entra: {
            clientId: 'bad81977-84cc-4d19-9492-3828d507b848',
            mode: 'online',
        },
    }
}

export interface OauthSlice extends OauthState, OauthActions {}
export var globalRefreshToken = null
var gloabalRefreshTokenCall: Promise<IResponse<ITokenResponse>> = null
export const createOauthSlice: StateCreator<
    BoundStore,
    [['zustand/devtools', never]],
    [],
    OauthSlice
> = (set, get) => {
    SocialLogin.initialize(getSocialConfig() as any)
    return {
        IDtoken: undefined,
        refreshToken: undefined,
        expiration: undefined,
        loginCompleted: false,
        credentials: {
            email: '',
            password: '',
            loading: false,
            errors: {},
        },

        googleOneTapSignIn: async () => {
            const googleResp = await SocialLogin.login({
                provider: 'google',

                options: { scopes: ['email', 'profile', 'openid'] },
            })

            const googleResult = googleResp.result as GoogleLoginResponseOnline

            // lets see if the user already exist
            const bodyFields = {
                grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
                client_id: oidcConfig().client_id,
                subject_token: googleResult.accessToken.token,
                subject_issuer: 'google',
                client_secret: oidcConfig().client_secret,
                subject_token_type:
                    'urn:ietf:params:oauth:token-type:access_token',
            }
            const resp = await keycloakClient().post<any, ITokenResponse>(
                OAUTH_ENDPOINTS.token,
                qs.stringify(bodyFields),
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                }
            )
            return {
                googleResult,
                resp,
            }
        },

        appleOneTapSignIn: async () => {
            const appleResp = await SocialLogin.login({
                provider: 'apple',
                options: { scopes: ['email', 'name'] },
            })
            const appleResult = appleResp.result as AppleProviderResponse
            // lets see if the user already exist
            const bodyFields = {
                grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
                client_id: oidcConfig().client_id,
                subject_token: appleResult.accessToken.token,
                subject_issuer: 'apple',
                client_secret: oidcConfig().client_secret,
                subject_token_type: 'urn:ietf:params:oauth:token-type:id_token',
            }
            const resp = await keycloakClient().post<any, ITokenResponse>(
                OAUTH_ENDPOINTS.token,
                qs.stringify(bodyFields),
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                }
            )
            return { appleResult, resp }
        },

        entraOneTapSignIn: async () => {
            const entraResp = await entraLogin()
            const entraResult = entraResp.result

            // lets see if the user already exist
            const bodyFields = {
                grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
                client_id: oidcConfig().client_id,
                subject_token: entraResult.accessToken.token,
                subject_issuer: 'microsoft',
                client_secret: oidcConfig().client_secret,
                subject_token_type:
                    'urn:ietf:params:oauth:token-type:access_token',
            }
            const resp = await keycloakClient().post<any, ITokenResponse>(
                OAUTH_ENDPOINTS.token,
                qs.stringify(bodyFields),
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                }
            )
            return { entraResult, resp }
        },
        refreshTokens: async () => {
            const { refreshToken, expiration } = get()

            if (refreshToken && expiration) {
                if (Date.now() < expiration - 20000) {
                    return { status: 'error', message: 'not expired' }
                }
                spLogTags('refreshing tokens', ['OAUTH'])
                if (gloabalRefreshTokenCall !== null) {
                    spLogTags(
                        'refreshing tokens already in progress returning existing promise',
                        ['OAUTH']
                    )
                    return gloabalRefreshTokenCall
                }
                const refreshTokenCall = async (): Promise<
                    IResponse<ITokenResponse>
                > => {
                    const bodyFields = {
                        grant_type: 'refresh_token',
                        client_id: oidcConfig().client_id,
                        client_secret: oidcConfig().client_secret,
                        refresh_token: refreshToken,
                    }
                    const resp = await keycloakClient().post<
                        any,
                        ITokenResponse
                    >(OAUTH_ENDPOINTS.token, bodyFields, {
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                        },
                    })
                    if (resp.status === 'success') {
                        const token = resp.data!
                        set(
                            {
                                IDtoken: token.access_token,
                                refreshToken: token.refresh_token,
                                expiration:
                                    Date.now() + token.expires_in * 1000,
                            },
                            false,
                            'refreshTokens'
                        )

                        return { status: 'success', data: token }
                    } else {
                        if (
                            resp.data &&
                            (resp.data as any).data.error === 'invalid_grant'
                        ) {
                            /// Request for new token failed as session not active
                            spLogTags(
                                'refreshing tokens failed as session not active, logging user out',
                                ['OAUTH'],
                                resp
                            )
                            get().logout()
                        }

                        spLogTags('refreshing tokens failed', ['OAUTH'], resp)
                        return { status: 'error', message: 'refresh failed' }
                    }
                }
                gloabalRefreshTokenCall = refreshTokenCall()

                return gloabalRefreshTokenCall.finally(() => {
                    gloabalRefreshTokenCall = null
                })
            } else {
                return { status: 'error', message: 'no refresh token' }
            }
        },
        signIn: async () => {
            loadingSignal.value = true
            const { email, password } = get().credentials
            /// Validate email
            if (!RegExpCheckEmail(email)) {
                set(
                    {
                        credentials: {
                            ...get().credentials,
                            errors: {
                                email: i18n.t(
                                    'authentication.sign-in.email-not-valid'
                                ),
                            },
                        },
                    },
                    false,
                    'error-email'
                )

                loadingSignal.value = false
                return
            }

            if (userPrefLoginMethod.peek() === 'email') {
                set(
                    {
                        credentials: {
                            ...get().credentials,
                            loading: true,
                        },
                    },
                    false,
                    'signIn'
                )

                await bs().emailSignIn(email)
            } else {
                /// Validate Password
                if (password === undefined || password === '') {
                    set(
                        {
                            credentials: {
                                ...get().credentials,
                                errors: {
                                    password: i18n.t(
                                        'authentication.sign-in.pass-required'
                                    ),
                                },
                            },
                        },
                        false,
                        'error-password'
                    )
                    loadingSignal.value = false
                    return
                } else {
                    set(
                        {
                            credentials: {
                                ...get().credentials,
                                loading: true,
                            },
                        },
                        false,
                        'signInWithPassword'
                    )
                    await bs().signInWithPassword(email, password, null)
                }
            }
            loadingSignal.value = false
        },
        emailSignIn: async (email: string) => {
            logEvent('sign_in_email', {})
            const nonce = uuidv1()
            const codeVerifier = getRandomString(43)

            const body: IEmailSignInBody = {
                email: email.toLowerCase(),
                client_id: oidcConfig().client_id,
                redirect_uri: oidcConfig().redirect_uri,
                expiration_seconds: 60000,
                nonce: nonce,
                state: codeVerifier,
                remember_me: true,
                scope: 'openid profile email offline_access',
            }
            const resp = await keycloakClient().post<IEmailSignInBody, null>(
                OAUTH_ENDPOINTS.sendEmailLogin,
                body,
                { timeout: 15000 }
            )

            if (resp.status === 'error') {
                set({
                    credentials: {
                        ...get().credentials,
                        errors: {
                            email: i18n.t(
                                'authentication.sign-in.email-not-valid'
                            ),
                        },
                        loading: false,
                    },
                })
                spLogTags(resp.message, ['ERROR', 'OAUTH'], resp)
                if (
                    resp.message?.includes('Network Error') ||
                    resp.message?.includes('timeout')
                ) {
                    defaultAddSnack(i18n.t('snack.connectionError'), 'error')
                } else {
                    defaultAddSnack(i18n.t('snack.noUserError'), 'error')
                }
            } else {
                set({
                    credentials: {
                        ...get().credentials,
                        email: email,
                        loading: false,
                    },
                })
                window.pushPath(
                    `/authentication/passwordlessEmailSent?email=${email}`
                )
            }
            return resp
        },
        signInWithPassword: async (
            email: string,
            password: string,
            redirectPath: string | null = Paths.home
        ) => {
            logEvent('sign_in_password', {})
            const str = `${oidcConfig().client_id}:${
                oidcConfig().client_secret
            }`
            const base64Auth = bytesToBase64(new TextEncoder().encode(str))
            const headers = {
                Authorization: `Basic ${base64Auth}`,
                'Content-Type': 'application/x-www-form-urlencoded',
            }
            const bodyFields = {
                grant_type: 'password',
                audience: oidcConfig().client_id,
                username: email,
                password: password,
            }
            const resp = await keycloakClient().post<any, ITokenResponse>(
                OAUTH_ENDPOINTS.token,
                bodyFields,
                { headers: headers }
            )
            if (resp.status === 'error') {
                set({
                    credentials: {
                        ...get().credentials,
                        errors: {
                            password: i18n.t(
                                'authentication.sign-in.pass-incorrect'
                            ),
                        },
                        loading: false,
                    },
                })
                spLogTags(resp.message, ['ERROR', 'OAUTH'], resp)
                if (resp?.message?.includes('Network Error')) {
                    defaultAddSnack(i18n.t('snack.connectionError'), 'error')
                } else {
                    defaultAddSnack(i18n.t('snack.noUserError'), 'error')
                }
            } else {
                set({
                    credentials: {
                        ...get().credentials,
                        loading: false,
                        password: '',
                    },
                })
                const token = resp.data!
                set({
                    IDtoken: token.access_token,
                    refreshToken: token.refresh_token,
                    expiration: Date.now() + token.expires_in * 1000,
                })
                await get().onLogin()

                if (redirectPath) {
                    window.pushPath(redirectPath)
                }
            }

            return resp
        },
        // this is a redirect to the keycloak login page sign in Dont use it! this is just to impersonate
        signInRedirect: async () => {
            const queryParams = queryString.stringify({
                client_id: oidcConfig().client_id,
                redirect_uri: oidcConfig().redirect_uri,
                response_type: 'code',
                scope: 'openid',
            })
            const url = `${
                oidcConfig().authority
            }/realms/master/protocol/openid-connect/auth?${queryParams}`
            window.location.href = url
        },

        exchangeCode: async (
            code: string
        ): Promise<IResponse<ITokenResponse>> => {
            const bodyFields: ICodeExchangeBody = {
                grant_type: 'authorization_code',
                client_id: oidcConfig().client_id,
                client_secret: oidcConfig().client_secret,
                redirect_uri: oidcConfig().redirect_uri,
                code: code,
            }
            const resp = await keycloakClient().post<
                ICodeExchangeBody,
                ITokenResponse
            >(OAUTH_ENDPOINTS.token, bodyFields, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            })
            if (resp.status === 'success') {
                const token = resp.data!
                set({
                    IDtoken: token.access_token,
                    refreshToken: token.refresh_token,
                    expiration: Date.now() + token.expires_in * 1000,
                })

                return { status: 'success' }
            }
            return resp
        },
        signInWithProvider: async (provider: string) => {
            let tokenResp
            let providerResult
            loadingSignal.value = true
            try {
                if (provider === 'google') {
                    const { googleResult, resp } =
                        await bs().googleOneTapSignIn()

                    tokenResp = resp
                    providerResult = {
                        email: googleResult.profile.email,
                        firstName: googleResult.profile.givenName,
                        lastName: googleResult.profile.familyName,
                    }
                }
                if (provider === 'apple') {
                    const { appleResult, resp } = await bs().appleOneTapSignIn()
                    tokenResp = resp
                    providerResult = {
                        email: appleResult.profile.email,
                        firstName: appleResult.profile.givenName,
                        lastName: appleResult.profile.familyName,
                    }
                }
                if (provider === 'entra') {
                    const { resp, entraResult } = await bs().entraOneTapSignIn()
                    tokenResp = resp
                    providerResult = {
                        email: entraResult.profile?.email,
                        firstName: entraResult.profile?.givenName,
                        lastName: entraResult.profile?.familyName,
                        phoneNumber: entraResult.profile?.mobilePhone,
                    }
                }
                const profile = await EventrGatewayAPI.profile.getMe({
                    headers: {
                        Authorization: `Bearer ${tokenResp.data.access_token}`,
                    },
                })
                if (profile.status === 'success') {
                    const token = tokenResp.data!
                    set({
                        IDtoken: token.access_token,
                        refreshToken: token.refresh_token,
                        expiration: Date.now() + token.expires_in * 1000,
                    })

                    window.pushPath(Paths.home)
                    await bs().onLogin()
                } else {
                    // take to account creation flow
                    const token = tokenResp.data!
                    useRegStore.getState().setInviteData({
                        email: providerResult.email,
                        firstName: providerResult.firstName,
                        lastName: providerResult.lastName,
                        phoneNumber: providerResult.phoneNumber,
                        isEIDP: true,
                        flow_type: 'create',
                        authTokens: {
                            IdToken: token.access_token,
                            refreshToken: token.refresh_token,
                            expiration: Date.now() + token.expires_in * 1000,
                        },
                    })
                    window.pushPath('/authentication/addDetails')
                }
            } finally {
                loadingSignal.value = false
            }
        },
        setTokens: (tokens: any) => {
            set({
                IDtoken: tokens.IdToken,
                refreshToken: tokens.refreshToken,
                expiration: tokens.expiration,
            })
        },
    }
}

export const keycloakClient = () => {
    const client = new ApiClient({
        baseURL: oidcConfig().authority,
    })

    client.setInterceptors(
        comboInterceptorResponse,
        logInterResponseError,
        logInterReq,
        logInterReqError
    )
    return client
}

export function getClaimsJWT() {
    const claims = bs()?.wpUser?.claimsJWT

    if (!claims || claims === 'null') {
        return null
    }
    return claims
}

export function setClaimsJWT(token: string) {
    if (!token) return
    const userData = bs().wpUser ?? null

    if (!userData) {
        return
    }

    if (userData.claimsJWT === token) return
    storeClaims(token)
    useBoundStore.setState({
        wpUser: {
            ...userData,
            claimsJWT: token,
        },
    })
}
export const storeClaims = (token?: string) => {
    if (!token) return
    // need to leave claims empty if in guest mode
    if (useGuestStore.getState().isGuestMode) return
    const claimsDecode = decodeJwtWeb(token)
    localStorage.setItem(
        SESSION_USER_CLAIMS,
        JSON.stringify(claimsDecode?.claims)
    )

    useBoundStore.setState({
        userClaims: claimsDecode?.claims,
        tier: claimsDecode?.tier,
    })
}

export const getToken = () => {
    return bs().IDtoken || undefined
}
useToken.getState().setTokenAccessFunction(getToken)
